import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../ui/data-table';
import { Button } from '../ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Input } from '../ui/input';
import { classAPI, courseAPI } from '../../services/api';

const Classes = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingClassId, setEditingClassId] = useState(null);
  const [deletingClassId, setDeletingClassId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    course_id: '',
    title: '',
    description: '',
    class_id: '',
    class_code: ''
  });

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false
    },
    {
      accessorKey: 'class_id',
      header: 'Class ID',
      sortable: true
    },
    // {
    //   accessorKey: 'class_code',
    //   header: 'Class Code',
    //   sortable: true
    // },
    {
      accessorKey: 'course_title',
      header: 'Course',
      sortable: true,
      cell: (classItem) => getCourseTitle(classItem.course_id)
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleViewClassDetails(row.id)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="View class details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleEditClass(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit class"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteClass(row.id)}
            disabled={deletingClassId === row.id}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete class"
          >
            {deletingClassId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await classAPI.getAllClasses();
      setClasses(response.data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };
  
  

  useEffect(() => {
    fetchClasses();
    fetchCourses();
  }, []);

  // Ensure courses are loaded when modal opens
  useEffect(() => {
    if (isModalOpen && courses.length === 0) {
      fetchCourses();
    }
  }, [isModalOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const submitData = {
        ...formData,
        course_id: parseInt(formData.course_id)
      };
      
      if (isEditing) {
        // Update existing class
        await classAPI.updateClass(editingClassId, submitData);
      } else {
        // Create new class
        await classAPI.createClass(submitData);
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} class:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClass = (classItem) => {
    setIsEditing(true);
    setEditingClassId(classItem.id);
    setFormData({
      course_id: classItem.course_id ? String(classItem.course_id) : '',
      title: classItem.title || '',
      description: classItem.description || '',
      class_id: classItem.class_id || '',
      class_code: classItem.class_code || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingClassId(classId);
      await classAPI.deleteClass(classId);
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class. Please try again.');
    } finally {
      setDeletingClassId(null);
    }
  };

  const handleViewClassDetails = (classId) => {
    navigate(`/classes/${classId}`);
  };

  const resetForm = () => {
    setFormData({
      course_id: '',
      title: '',
      description: '',
      class_id: '',
      class_code: ''
    });
    setIsEditing(false);
    setEditingClassId(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const getCourseTitle = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.title : 'Unknown Course';
  };

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Classes</h1>
            <p className="text-lg text-gray-600">Manage course classes</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            + Add Class
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={classes}
        searchKey="title"
        searchPlaceholder="Search classes..."
        loading={loading}
        emptyMessage="No classes found."
      />

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Class' : 'Add New Class'}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter class title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Enter class description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="class_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Class ID *
                </label>
                <Input
                  id="class_id"
                  name="class_id"
                  type="text"
                  value={formData.class_id}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., PROG101A"
                />
              </div>

              {/* <div>
                <label htmlFor="class_code" className="block text-sm font-medium text-gray-700 mb-1">
                  Class Code *
                </label>
                <Input
                  id="class_code"
                  name="class_code"
                  type="text"
                  value={formData.class_code}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., PROG2024A"
                />
              </div> */}
            </div>

            <div>
              <label htmlFor="course_id" className="block text-sm font-medium text-gray-700 mb-1">
                Course *
              </label>
              <select
                id="course_id"
                name="course_id"
                value={formData.course_id}
                onChange={handleInputChange}
                required
                disabled={coursesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {coursesLoading ? 'Loading courses...' : 'Select a course'}
                </option>
                {!coursesLoading && courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || `Course ${course.id}`}
                  </option>
                ))}
              </select>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={handleCloseModal}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Class' : 'Create Class')
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default Classes;
